// Чистата логика на филтрите в заглавията на колоните (справките на склада):
// описанието на филтър (списък с отметки или диапазон от–до), проверката на
// ред, фасетните броячи и текстът за лентата „Филтри: …" над таблицата.
//
// Фасетни броячи: броят до всяка опция е колко реда ще останат, ако се
// отметне САМО тя при ВСИЧКИ ОСТАНАЛИ активни филтри (и търсенето) — затова
// броячът на колоната не зависи от собствения ѝ избор. Опциите идват от целия
// период (`universe`), така че стойност с 0 при текущите филтри не изчезва от
// списъка, а стои приглушена; вече отметната стойност, която вече я няма в
// периода, също остава — за да може да се махне.

export type NumberRange = { min: number | null; max: number | null };
export const EMPTY_RANGE: NumberRange = { min: null, max: null };

export type FilterValue = string[] | NumberRange;
export type FilterState = Record<string, FilterValue>;

// Филтър-списък: отметки със стойностите на реда (редът може да носи
// няколко — напр. складовете на доставка, разпределена в два склада)
export type ListFilterSpec<T> = {
  kind: "list";
  title: string;
  valuesOf: (row: T) => string[];
  labelOf?: (value: string) => string;
  // Подредба по смисъл (под → близо → над); иначе по азбука
  order?: readonly string[];
  // Тази стойност (обикновено „—" = липсва) не е опция
  skipValue?: string;
  // Търсачка над опциите; по подразбиране — при повече от 8
  searchable?: boolean;
};

// Филтър-диапазон за пари/количества: „от" и „до" включително
export type RangeFilterSpec<T> = {
  kind: "range";
  title: string;
  valueOf: (row: T) => number;
  format?: (value: number) => string;
};

export type FilterSpec<T> = ListFilterSpec<T> | RangeFilterSpec<T>;

export type FilterSpecs<T, S extends FilterState> = {
  [K in keyof S]: S[K] extends NumberRange ? RangeFilterSpec<T> : ListFilterSpec<T>;
};

export type FacetOption = { value: string; label: string; count: number };

// Над толкова опции списъкът получава собствена търсачка
export const SEARCH_THRESHOLD = 8;

// Сумите идват с плаваща запетая — „до 10.00" трябва да хване 9.999999
const EPSILON = 0.005;

export const isRangeValue = (value: FilterValue): value is NumberRange => !Array.isArray(value);

export const isFilterActive = (value: FilterValue) =>
  isRangeValue(value) ? value.min != null || value.max != null : value.length > 0;

// Празната стойност на същия вид (за „Изчисти" на една колона)
export const emptyValueOf = (value: FilterValue): FilterValue =>
  isRangeValue(value) ? EMPTY_RANGE : [];

export const clearFilterState = <S extends FilterState>(state: S): S =>
  Object.fromEntries(
    Object.entries(state).map(([key, value]) => [key, emptyValueOf(value)]),
  ) as S;

export const activeFilterCount = (state: FilterState) =>
  Object.values(state).filter(isFilterActive).length;

export const inRange = (value: number, range: NumberRange) =>
  (range.min == null || value >= range.min - EPSILON) &&
  (range.max == null || value <= range.max + EPSILON);

const matchesSpec = <T>(spec: FilterSpec<T>, value: FilterValue, row: T) => {
  if (!isFilterActive(value)) return true;
  if (spec.kind === "range") return isRangeValue(value) && inRange(spec.valueOf(row), value);
  const selected = value as string[];
  return spec.valuesOf(row).some((rowValue) => selected.includes(rowValue));
};

// Редът минава всички филтри (без `skipKey` — за фасетния брояч на колоната)
export const matchesColumnFilters = <T, S extends FilterState>(
  row: T,
  specs: FilterSpecs<T, S>,
  state: S,
  skipKey?: keyof S,
) =>
  (Object.keys(specs) as (keyof S & string)[]).every(
    (key) => key === skipKey || matchesSpec(specs[key] as FilterSpec<T>, state[key], row),
  );

export const applyColumnFilters = <T, S extends FilterState>(
  rows: T[],
  specs: FilterSpecs<T, S>,
  state: S,
) => rows.filter((row) => matchesColumnFilters(row, specs, state));

// Колко реда остават при всички филтри БЕЗ този (броячът „Всички" на сегмент)
export const facetTotal = <T, S extends FilterState>(
  base: T[],
  specs: FilterSpecs<T, S>,
  state: S,
  key: keyof S,
) => base.filter((row) => matchesColumnFilters(row, specs, state, key)).length;

type FacetArgs<T, S extends FilterState> = {
  // Всичко от периода — от тук идват опциите
  universe: T[];
  // Периодът след търсенето (и сегмента) — от тук идват броячите
  base: T[];
  specs: FilterSpecs<T, S>;
  state: S;
  key: keyof S & string;
};

export const facetOptions = <T, S extends FilterState>({
  universe,
  base,
  specs,
  state,
  key,
}: FacetArgs<T, S>): FacetOption[] => {
  const spec = specs[key] as ListFilterSpec<T>;
  const counts = new Map<string, number>();
  const note = (value: string, add: number) => {
    if (value === spec.skipValue) return;
    counts.set(value, (counts.get(value) ?? 0) + add);
  };
  for (const row of universe) for (const value of spec.valuesOf(row)) note(value, 0);
  for (const row of base) {
    if (!matchesColumnFilters(row, specs, state, key)) continue;
    for (const value of new Set(spec.valuesOf(row))) note(value, 1);
  }
  for (const value of state[key] as string[]) note(value, 0);

  const labelOf = spec.labelOf ?? ((value: string) => value);
  const options = [...counts.entries()].map(([value, count]) => ({
    value,
    label: labelOf(value),
    count,
  }));
  const rank = (value: string) => {
    const index = spec.order?.indexOf(value) ?? -1;
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };
  return options.sort(
    (a, b) => rank(a.value) - rank(b.value) || a.label.localeCompare(b.label, "bg", { numeric: true }),
  );
};

// Най-малката и най-голямата стойност в периода — подсказки в полетата от/до
export const rangeBounds = <T>(rows: T[], spec: RangeFilterSpec<T>): [number, number] | null => {
  if (!rows.length) return null;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const row of rows) {
    const value = spec.valueOf(row);
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return [min, max];
};

// Поле „от"/„до": празно → без граница; приема запетая и точка, интервали
// (`\s` хваща и непрекъсваемите интервали от formatMoney)
export const parseRangeInput = (raw: string): number | null => {
  const cleaned = raw.replace(/\s/g, "").replace(",", ".");
  if (!cleaned) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

// Текстът на активен филтър за лентата над таблицата:
// „Плащане: Неплатени" · „Доставчик" + брояч 2 · „Бруто: 100.00 – 500.00"
export type FilterDescription = { title: string; detail: string | null; count: number | null };

export const describeFilter = <T>(spec: FilterSpec<T>, value: FilterValue): FilterDescription | null => {
  if (!isFilterActive(value)) return null;
  if (spec.kind === "range" && isRangeValue(value)) {
    const format = spec.format ?? ((number: number) => String(number));
    const detail =
      value.min != null && value.max != null
        ? `${format(value.min)} – ${format(value.max)}`
        : value.min != null
          ? `≥ ${format(value.min)}`
          : `≤ ${format(value.max as number)}`;
    return { title: spec.title, detail, count: null };
  }
  const selected = value as string[];
  if (spec.kind === "list" && selected.length === 1) {
    const labelOf = spec.labelOf ?? ((option: string) => option);
    return { title: spec.title, detail: labelOf(selected[0]), count: null };
  }
  return { title: spec.title, detail: null, count: selected.length };
};

// Видимите опции при търсене в списъка: всяка дума трябва да се среща
export const filterOptionsByQuery = (options: FacetOption[], query: string) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return options;
  return options.filter((option) => {
    const label = option.label.toLowerCase();
    return words.every((word) => label.includes(word));
  });
};
